import { useMemo, useState } from "react"
import { AlertCircle, Check, ChevronDown, Loader2, Search, X } from "lucide-react"

import { CohortAvatar } from "components/mentorship/CohortExperience"
import { Button } from "components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu"
import { Input } from "components/ui/input"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "components/ui/sheet"
import { cn } from "lib/utils"
import type { Person } from "./types"

export type MentorPairingUpdate = {
  userId: number
  mentorId: number | null
}

type MentorPairingsManagerProps = {
  mode: "desktop" | "mobile"
  open: boolean
  roster: Person[]
  mentorLabelSingular: string
  mentorLabelPlural: string
  memberLabelSingular: string
  memberLabelPlural: string
  loading?: boolean
  onOpenChange: (open: boolean) => void
  onSave: (updates: MentorPairingUpdate[]) => Promise<void>
}

type PairingFilter = "all" | "unpaired"
type PairingDraft = Record<number, number | null>

const byName = (first: Person, second: Person) => first.name.localeCompare(second.name, undefined, { sensitivity: "base", numeric: true })

const makeDraft = (members: Person[]): PairingDraft => Object.fromEntries(
  members.map((member) => [member.id, member.mentorId ?? null]),
) as PairingDraft

function PairingSkeleton({ mobile = false }: { mobile?: boolean }) {
  return (
    <div role="status" aria-label="Loading members">
      <span className="sr-only">Loading members</span>
      {[0, 1, 2].map((row) => (
        <div key={row} className={cn("flex items-center gap-4 border-b border-[var(--cohort-rule-soft)]", mobile ? "px-5 py-4" : "px-7 py-4")}>
          <span className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-[var(--cohort-skeleton)]" />
          <span className="min-w-0 flex-1">
            <span className="block h-3 w-36 animate-pulse rounded bg-[var(--cohort-skeleton)]" />
            <span className="mt-2 block h-2.5 w-20 animate-pulse rounded bg-[var(--cohort-skeleton)]" />
          </span>
          <span className={cn("animate-pulse rounded-xl bg-[var(--cohort-skeleton)]", mobile ? "h-11 w-20" : "h-11 w-60")} />
        </div>
      ))}
    </div>
  )
}

function EmptyPairingList({
  query,
  filter,
  memberLabelPlural,
  mentorLabelSingular,
  onReset,
}: {
  query: string
  filter: PairingFilter
  memberLabelPlural: string
  mentorLabelSingular: string
  onReset: () => void
}) {
  const trimmedQuery = query.trim()
  const searching = Boolean(trimmedQuery)
  return (
    <div className="flex min-h-52 flex-col items-center justify-center px-5 py-12 text-center">
      <p className="text-xl">
        {searching ? `No matches for “${trimmedQuery}”` : `No unpaired ${memberLabelPlural.toLowerCase()}`}
      </p>
      <p className="mt-2 max-w-sm text-sm leading-6 text-[var(--cohort-ink-muted)]">
        {searching
          ? "Search looks at member and mentor names in this cohort."
          : `Everyone in this cohort has a ${mentorLabelSingular.toLowerCase()} assigned in the current draft.`}
      </p>
      <Button type="button" variant="outline" onClick={onReset} className="cohort-secondary-button mt-3 px-4">
        {searching ? "Clear search" : filter === "unpaired" ? `Show all ${memberLabelPlural.toLowerCase()}` : "Clear search"}
      </Button>
    </div>
  )
}

function NoMembersState({ memberLabelPlural }: { memberLabelPlural: string }) {
  return (
    <div className="flex min-h-60 flex-col items-center justify-center px-5 py-12 text-center">
      <p className="text-xl">No {memberLabelPlural.toLowerCase()} to pair yet</p>
      <p className="mt-2 max-w-sm text-sm leading-6 text-[var(--cohort-ink-muted)]">
        Once {memberLabelPlural.toLowerCase()} join this cohort they will appear here for mentor assignment.
      </p>
    </div>
  )
}

function SaveError() {
  return (
    <div role="alert" className="flex gap-2.5 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] px-3.5 py-3 text-sm leading-[1.55] text-[var(--cohort-clay)]">
      <AlertCircle className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden="true" />
      <p>We couldn&apos;t save these pairings. Your changes are still here. Try again.</p>
    </div>
  )
}

function NoMentorsNotice({
  mentorLabelSingular,
  mentorLabelPlural,
}: {
  mentorLabelSingular: string
  mentorLabelPlural: string
}) {
  return (
    <div className="border-b border-[var(--cohort-rule)] bg-[var(--cohort-rest)] px-5 py-7 text-center sm:px-7">
      <p className="text-xl">No {mentorLabelPlural.toLowerCase()} are available in this cohort.</p>
      <p className="mt-2 text-sm leading-6 text-[var(--cohort-ink-muted)]">
        A cohort member must have the {mentorLabelSingular.toLowerCase()} role before pairings can be created.
      </p>
    </div>
  )
}

function MentorOption({
  mentor,
  selected,
  count,
  memberLabelSingular,
  memberLabelPlural,
  noMentorLabel,
}: {
  mentor: Person | null
  selected: boolean
  count: number
  memberLabelSingular: string
  memberLabelPlural: string
  noMentorLabel?: string
}) {
  return (
    <>
      {mentor ? (
        <CohortAvatar person={mentor} size="md" tone="mentor" />
      ) : (
        <span aria-hidden="true" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--cohort-rule)] bg-[#f1ede2] text-sm text-[var(--cohort-ochre)]">—</span>
      )}
      <span className="min-w-0 flex-1 text-left">
        <span className={cn("block truncate text-[15px]", selected && "font-semibold")}>{mentor?.name || noMentorLabel || "No mentor"}</span>
        <span className="mt-0.5 block text-[13px] text-[var(--cohort-ink-muted)]">
          {mentor ? `· ${count} ${(count === 1 ? memberLabelSingular : memberLabelPlural).toLowerCase()}` : "clears the pairing"}
        </span>
      </span>
      {selected ? (
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[13px] text-[var(--cohort-orange-ink)]">
          Selected <Check className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
    </>
  )
}

export function MentorPairingsManager({
  mode,
  open,
  roster,
  mentorLabelSingular,
  mentorLabelPlural,
  memberLabelSingular,
  memberLabelPlural,
  loading = false,
  onOpenChange,
  onSave,
}: MentorPairingsManagerProps) {
  const mentors = useMemo(() => roster.filter((person) => person.roleKey === "mentor").sort(byName), [roster])
  const members = useMemo(() => roster.filter((person) => person.roleKey === "mentee" || !person.roleKey).sort(byName), [roster])
  const mentorById = useMemo(() => new Map(mentors.map((mentor) => [mentor.id, mentor])), [mentors])
  const [baseline] = useState<PairingDraft>(() => makeDraft(members))
  const [draft, setDraft] = useState<PairingDraft>(() => makeDraft(members))
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<PairingFilter>("all")
  const [phase, setPhase] = useState<"editing" | "saving" | "error">("editing")
  const [discardOpen, setDiscardOpen] = useState(false)
  const [desktopPickerId, setDesktopPickerId] = useState<number | null>(null)
  const [mobilePickerId, setMobilePickerId] = useState<number | null>(null)
  const [mobilePickerQuery, setMobilePickerQuery] = useState("")
  const [invalidMemberIds, setInvalidMemberIds] = useState<Set<number>>(new Set())

  const isSaving = phase === "saving"
  const changedMemberIds = useMemo(
    () => members.filter((member) => (draft[member.id] ?? null) !== (baseline[member.id] ?? null)).map((member) => member.id),
    [baseline, draft, members],
  )
  const hasChanges = changedMemberIds.length > 0
  const isEligibleAssignment = (mentorId: number | null | undefined) => mentorId != null && mentorById.has(mentorId)
  const pairedCount = members.filter((member) => isEligibleAssignment(draft[member.id])).length
  const unpairedCount = members.length - pairedCount
  const mentorCounts = useMemo(() => {
    const counts = new Map<number, number>()
    mentors.forEach((mentor) => counts.set(mentor.id, 0))
    members.forEach((member) => {
      const mentorId = draft[member.id]
      if (mentorId != null && counts.has(mentorId)) counts.set(mentorId, (counts.get(mentorId) || 0) + 1)
    })
    return counts
  }, [draft, members, mentors])

  const visibleMembers = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return members
      .filter((member) => {
        const mentorId = draft[member.id]
        const assignedMentor = mentorId != null ? mentorById.get(mentorId) : null
        if (filter === "unpaired" && assignedMentor) return false
        if (!needle) return true
        return member.name.toLowerCase().includes(needle)
          || assignedMentor?.name.toLowerCase().includes(needle)
          || member.mentorName?.toLowerCase().includes(needle)
      })
      .sort((first, second) => {
        const firstMentorId = draft[first.id]
        const secondMentorId = draft[second.id]
        const firstRank = firstMentorId != null && !mentorById.has(firstMentorId) ? 0 : mentorById.has(firstMentorId as number) ? 2 : 1
        const secondRank = secondMentorId != null && !mentorById.has(secondMentorId) ? 0 : mentorById.has(secondMentorId as number) ? 2 : 1
        return firstRank - secondRank || byName(first, second)
      })
  }, [draft, filter, members, mentorById, query])

  const updateAssignment = (memberId: number, mentorId: number | null) => {
    setDraft((current) => ({ ...current, [memberId]: mentorId }))
    setInvalidMemberIds((current) => {
      if (!current.has(memberId)) return current
      const next = new Set(current)
      next.delete(memberId)
      return next
    })
    setPhase("editing")
  }

  const requestClose = () => {
    if (isSaving) return
    if (hasChanges) {
      setDiscardOpen(true)
      return
    }
    onOpenChange(false)
  }

  const handleSave = async () => {
    if (!hasChanges || isSaving) return
    const updates = changedMemberIds.map((userId) => ({ userId, mentorId: draft[userId] ?? null }))
    setPhase("saving")
    setInvalidMemberIds(new Set())
    try {
      await onSave(updates)
      onOpenChange(false)
    } catch (error: any) {
      const rowErrors = Array.isArray(error?.response?.data?.pairings) ? error.response.data.pairings : []
      setInvalidMemberIds(new Set(
        rowErrors.flatMap((row: Record<string, unknown>, index: number) => Object.keys(row || {}).length && updates[index] ? [updates[index].userId] : []),
      ))
      setPhase("error")
    }
  }

  const resetSearch = () => {
    setQuery("")
    setFilter("all")
  }

  const renderFilters = (mobile: boolean) => members.length ? (
    <div role="group" aria-label={`Filter ${memberLabelPlural.toLowerCase()}`} className={cn("flex gap-2", mobile ? "mt-3" : "rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-1")}>
      <button
        type="button"
        aria-pressed={filter === "all"}
        onClick={() => setFilter("all")}
        className={cn(
          "min-h-9 rounded-[9px] px-3.5 text-sm",
          mobile && "min-h-11 rounded-xl border px-4 text-[15px]",
          filter === "all"
            ? "border-[var(--cohort-forest)] bg-[var(--cohort-forest)] font-semibold text-white"
            : "border-[var(--cohort-rule)] bg-[var(--cohort-rest)] text-[var(--cohort-ink-muted)]",
        )}
      >All {members.length}</button>
      <button
        type="button"
        aria-pressed={filter === "unpaired"}
        disabled={unpairedCount === 0}
        onClick={() => setFilter("unpaired")}
        className={cn(
          "min-h-9 rounded-[9px] px-3.5 text-sm disabled:cursor-not-allowed disabled:opacity-45",
          mobile && "min-h-11 rounded-xl border px-4 text-[15px]",
          filter === "unpaired"
            ? "border-[var(--cohort-forest)] bg-[var(--cohort-forest)] font-semibold text-white"
            : "border-[var(--cohort-rule)] bg-[var(--cohort-rest)] text-[var(--cohort-ink-muted)]",
        )}
      >Unpaired {unpairedCount}</button>
    </div>
  ) : null

  const renderSearch = () => members.length ? (
    <div className="relative min-w-0 flex-1">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--cohort-ink-muted)]" aria-hidden="true" />
      <Input
        type="search"
        aria-label={`Search ${memberLabelPlural.toLowerCase()} or ${mentorLabelPlural.toLowerCase()}`}
        placeholder={`Search ${memberLabelPlural.toLowerCase()} or ${mentorLabelPlural.toLowerCase()}`}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="min-h-11 rounded-xl border-[var(--cohort-rule)] bg-white pl-10 text-[15px] shadow-none"
      />
    </div>
  ) : null

  const rowState = (member: Person) => {
    const mentorId = draft[member.id] ?? null
    const mentor = mentorId != null ? mentorById.get(mentorId) || null : null
    const changed = (mentorId ?? null) !== (baseline[member.id] ?? null)
    const stale = mentorId != null && !mentor
    const invalid = invalidMemberIds.has(member.id)
    return { mentorId, mentor, changed, stale, invalid }
  }

  const renderDesktopRow = (member: Person) => {
    const state = rowState(member)
    const tag = state.invalid ? "Needs review" : state.changed ? "Changed" : state.stale ? "Needs reassignment" : state.mentor ? "" : `Needs ${mentorLabelSingular.toLowerCase()}`
    return (
      <div key={member.id} className={cn(
        "relative flex min-h-[68px] items-center gap-4 border-b border-[var(--cohort-rule-soft)] px-7 py-3 last:border-b-0 hover:bg-[var(--cohort-wash)]",
        state.changed && "bg-[#fdfbf4]",
      )}>
        <CohortAvatar person={member} size="row" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-medium">{member.name}</span>
          <span className="mt-0.5 block text-[13px] text-[var(--cohort-ink-muted)]">{memberLabelSingular}</span>
        </span>
        <span className={cn("w-28 shrink-0 text-right text-[13px]", tag && "text-[var(--cohort-ochre)]", state.invalid && "text-[var(--cohort-clay)]")}>{tag}</span>
        <span className="w-[264px] shrink-0">
          <span className="mb-1 block text-xs uppercase tracking-[0.04em] text-[var(--cohort-ink-muted)]">Assigned {mentorLabelSingular.toLowerCase()}</span>
          <DropdownMenu
            modal={false}
            open={desktopPickerId === member.id}
            onOpenChange={(nextOpen) => setDesktopPickerId(nextOpen ? member.id : null)}
          >
            <DropdownMenuTrigger asChild disabled={(!mentors.length && state.mentorId == null) || isSaving}>
              <button
                type="button"
                aria-label={`Assigned ${mentorLabelSingular.toLowerCase()} for ${member.name}`}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => {
                  if ((!mentors.length && state.mentorId == null) || isSaving) return
                  setDesktopPickerId((current) => current === member.id ? null : member.id)
                }}
                className={cn(
                  "flex min-h-11 w-full items-center gap-2.5 rounded-xl border bg-white px-3 text-left hover:border-[var(--cohort-action)] disabled:cursor-not-allowed disabled:bg-[var(--cohort-muted-fill)] disabled:text-[var(--cohort-muted-ink)]",
                  state.changed ? "border-[#c9ae6a] bg-[#fdfbf4]" : state.stale || state.invalid ? "border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)]" : state.mentor ? "border-[var(--cohort-rule)]" : "border-dashed border-[#c8b79a] bg-[#fcfaf4]",
                )}
              >
                {state.stale ? (
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] text-[var(--cohort-clay)] line-through">{member.mentorName || "Previous mentor"}</span>
                    <span className="block text-xs text-[var(--cohort-clay)]">No longer in this cohort</span>
                  </span>
                ) : (
                  <>
                    {state.mentor ? <CohortAvatar person={state.mentor} size="sm" tone="mentor" /> : <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[var(--cohort-rule)] bg-[#f1ede2] text-[var(--cohort-ochre)]">—</span>}
                    <span className={cn("min-w-0 flex-1 truncate text-[15px]", !state.mentor && "text-[var(--cohort-ochre)]")}>{state.mentor?.name || `No ${mentorLabelSingular.toLowerCase()} assigned`}</span>
                  </>
                )}
                {state.changed ? <span className="text-xs text-[var(--cohort-ochre)]">Changed</span> : null}
                <ChevronDown className="h-4 w-4 shrink-0 text-[var(--cohort-ink-muted)]" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent portalled={false} align="end" className="cohort-experience pointer-events-auto !z-[520] max-h-[262px] w-[300px] overflow-y-auto rounded-[14px] border border-[var(--cohort-rule)] bg-white p-1.5 shadow-[0_20px_40px_-14px_rgba(16,20,17,0.35)]">
              <DropdownMenuItem onSelect={() => { updateAssignment(member.id, null); setDesktopPickerId(null) }} className={cn("min-h-11 gap-2.5 px-2.5", state.mentorId == null && "bg-[var(--cohort-orange-soft)]")}>
                <MentorOption mentor={null} selected={state.mentorId == null} count={0} memberLabelSingular={memberLabelSingular} memberLabelPlural={memberLabelPlural} noMentorLabel={`No ${mentorLabelSingular.toLowerCase()}`} />
              </DropdownMenuItem>
              {mentors.map((mentor) => (
                <DropdownMenuItem key={mentor.id} onSelect={() => { updateAssignment(member.id, mentor.id); setDesktopPickerId(null) }} className={cn("min-h-11 gap-2.5 px-2.5", state.mentorId === mentor.id && "bg-[var(--cohort-orange-soft)]")}>
                  <MentorOption mentor={mentor} selected={state.mentorId === mentor.id} count={mentorCounts.get(mentor.id) || 0} memberLabelSingular={memberLabelSingular} memberLabelPlural={memberLabelPlural} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>
    )
  }

  const renderMobileRow = (member: Person) => {
    const state = rowState(member)
    const tag = state.invalid ? "Needs review" : state.changed ? "Changed" : state.stale ? "Needs reassignment" : state.mentor ? "" : `Needs ${mentorLabelSingular.toLowerCase()}`
    return (
      <div key={member.id} className={cn("border-b border-[var(--cohort-rule-soft)] px-5 py-3.5", state.changed && "bg-[#fdfbf4]")}>
        <div className="flex items-center gap-3">
          <CohortAvatar person={member} size="row" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-medium">{member.name}</span>
            <span className="mt-0.5 block text-[13px] text-[var(--cohort-ink-muted)]">{memberLabelSingular}</span>
          </span>
          <span className={cn("shrink-0 text-[13px] text-[var(--cohort-ochre)]", state.invalid && "text-[var(--cohort-clay)]")}>{tag}</span>
        </div>
        <div className="mt-2.5 flex items-center justify-between gap-3 pl-[52px]">
          <span className="min-w-0 flex-1">
            <span className="block text-xs uppercase tracking-[0.04em] text-[var(--cohort-ink-muted)]">Assigned {mentorLabelSingular.toLowerCase()}</span>
            <span className={cn("block truncate text-[15px]", !state.mentor && "text-[var(--cohort-ochre)]", state.stale && "text-[var(--cohort-clay)] line-through")}>{state.mentor?.name || (state.stale ? member.mentorName || "Previous mentor" : `No ${mentorLabelSingular.toLowerCase()} assigned`)}</span>
            {state.stale ? <span className="block text-xs text-[var(--cohort-clay)]">No longer in this cohort</span> : null}
          </span>
          <button
            type="button"
            disabled={(!mentors.length && state.mentorId == null) || isSaving}
            aria-label={`${state.mentor || state.stale ? "Change" : "Assign"} assigned ${mentorLabelSingular.toLowerCase()} for ${member.name}`}
            onClick={() => {
              setMobilePickerQuery("")
              setMobilePickerId(member.id)
            }}
            className="min-h-11 shrink-0 rounded-xl border border-[var(--cohort-secondary-border)] bg-[var(--cohort-secondary-fill)] px-3.5 text-[15px] font-semibold text-[var(--cohort-action)] disabled:cursor-not-allowed disabled:bg-[var(--cohort-muted-fill)] disabled:text-[var(--cohort-muted-ink)]"
          >{state.mentor || state.stale ? "Change" : "Assign"}</button>
        </div>
      </div>
    )
  }

  const listContent = (mobile: boolean) => {
    if (loading) return <PairingSkeleton mobile={mobile} />
    if (!members.length) return <NoMembersState memberLabelPlural={memberLabelPlural} />
    return (
      <>
        {!mentors.length ? <NoMentorsNotice mentorLabelSingular={mentorLabelSingular} mentorLabelPlural={mentorLabelPlural} /> : null}
        {visibleMembers.length
          ? visibleMembers.map((member) => mobile ? renderMobileRow(member) : renderDesktopRow(member))
          : <EmptyPairingList query={query} filter={filter} memberLabelPlural={memberLabelPlural} mentorLabelSingular={mentorLabelSingular} onReset={resetSearch} />}
      </>
    )
  }

  const discardActions = (stacked: boolean) => (
    <div className={cn("mt-5 flex gap-2.5", stacked ? "flex-col" : "justify-end")}>
      <Button type="button" variant="outline" onClick={() => setDiscardOpen(false)} className="cohort-secondary-button min-h-[46px] px-4">Keep editing</Button>
      <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="cohort-secondary-button cohort-destructive-secondary-button min-h-[46px] border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] px-4 text-[var(--cohort-clay)]">Discard changes</Button>
    </div>
  )

  const mobilePickerMember = mobilePickerId != null ? members.find((member) => member.id === mobilePickerId) || null : null
  const mobilePickerMentors = mentors.filter((mentor) => mentor.name.toLowerCase().includes(mobilePickerQuery.trim().toLowerCase()))

  if (mode === "mobile") {
    return (
      <>
        <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) requestClose() }}>
          <SheetContent side="bottom" overlayClassName="cohort-pairings-manager-overlay" className="cohort-experience cohort-pairings-mobile-sheet !inset-0 !h-[100dvh] !max-h-none !w-screen !max-w-none !gap-0 !overflow-hidden !rounded-none !border-0 !bg-[var(--cohort-paper)] !p-0 !text-[var(--cohort-ink)]">
            <div className="shrink-0 px-5 pt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle className="text-[25px] font-normal leading-[1.15] tracking-[-0.02em]">Manage {mentorLabelSingular.toLowerCase()} pairings</SheetTitle>
                  <SheetDescription className="mt-1 text-sm leading-[1.5] text-[var(--cohort-ink-muted)]">Assign one {mentorLabelSingular.toLowerCase()} to each eligible cohort {memberLabelSingular.toLowerCase()}.</SheetDescription>
                </div>
                <button type="button" disabled={isSaving} onClick={requestClose} className="cohort-icon-button flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)]" aria-label={`Close manage ${mentorLabelSingular.toLowerCase()} pairings`}><X className="h-[18px] w-[18px]" aria-hidden="true" /></button>
              </div>
              <p aria-live="polite" className="mt-3 text-sm text-[var(--cohort-ink-muted)]"><span className="font-medium text-[var(--cohort-ink)]">{pairedCount} paired</span><span> · {unpairedCount} unpaired</span></p>
              {members.length ? <div className="mt-3">{renderSearch()}{renderFilters(true)}</div> : null}
              <div className="mt-3.5 h-px bg-[var(--cohort-rule)]" />
            </div>
            <div className="cohort-pairings-list min-h-0 flex-1 overflow-y-auto bg-[var(--cohort-rest)]">{listContent(true)}</div>
            <div className="shrink-0 border-t border-[var(--cohort-rule)] bg-[var(--cohort-paper)] px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3">
              {phase === "error" ? <div className="mb-2.5"><SaveError /></div> : null}
              <div className="flex gap-2.5">
                <Button type="button" variant="outline" disabled={isSaving} onClick={requestClose} className="cohort-secondary-button min-h-[50px] px-5 text-base">Cancel</Button>
                <Button type="button" disabled={!hasChanges || isSaving || loading} onClick={handleSave} className="cohort-primary-button min-h-[50px] flex-1 text-base">
                  {isSaving ? <Loader2 className="h-[17px] w-[17px] animate-spin" aria-hidden="true" /> : null}{isSaving ? "Saving…" : "Save pairings"}
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={Boolean(mobilePickerMember)} onOpenChange={(nextOpen) => { if (!nextOpen) setMobilePickerId(null) }}>
          <SheetContent side="bottom" overlayClassName="cohort-pairings-nested-overlay" className="cohort-experience cohort-pairings-picker-sheet !max-h-[min(78dvh,660px)] !gap-0 !overflow-hidden !rounded-t-[20px] !border-0 !bg-[var(--cohort-paper)] !p-0 !text-[var(--cohort-ink)]">
            <div className="shrink-0 px-5 pb-3 pt-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <SheetTitle className="text-[21px] font-normal tracking-[-0.015em]">Choose a {mentorLabelSingular.toLowerCase()}</SheetTitle>
                  <SheetDescription className="mt-0.5 text-[13px] text-[var(--cohort-ink-muted)]">for {mobilePickerMember?.name}</SheetDescription>
                </div>
                <button type="button" onClick={() => setMobilePickerId(null)} aria-label={`Close ${mentorLabelSingular.toLowerCase()} picker`} className="cohort-icon-button flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)]"><X className="h-[18px] w-[18px]" aria-hidden="true" /></button>
              </div>
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--cohort-ink-muted)]" aria-hidden="true" />
                <Input type="search" aria-label={`Search ${mentorLabelPlural.toLowerCase()}`} placeholder={`Search ${mentorLabelPlural.toLowerCase()}`} value={mobilePickerQuery} onChange={(event) => setMobilePickerQuery(event.target.value)} className="min-h-[46px] rounded-xl border-[var(--cohort-rule)] bg-white pl-10 text-[15px] shadow-none" />
              </div>
            </div>
            <div role="listbox" aria-label={mentorLabelPlural} className="cohort-pairings-list min-h-0 flex-1 overflow-y-auto border-t border-[var(--cohort-rule)] bg-[var(--cohort-rest)]">
              {mobilePickerMember ? (
                <button type="button" role="option" aria-selected={(draft[mobilePickerMember.id] ?? null) == null} onClick={() => { updateAssignment(mobilePickerMember.id, null); setMobilePickerId(null) }} className={cn("flex min-h-[60px] w-full items-center gap-3 border-b border-[var(--cohort-rule-soft)] px-5 text-left", (draft[mobilePickerMember.id] ?? null) == null && "bg-[var(--cohort-orange-soft)]")}>
                  <MentorOption mentor={null} selected={(draft[mobilePickerMember.id] ?? null) == null} count={0} memberLabelSingular={memberLabelSingular} memberLabelPlural={memberLabelPlural} noMentorLabel={`No ${mentorLabelSingular.toLowerCase()}`} />
                </button>
              ) : null}
              {mobilePickerMentors.map((mentor) => {
                const selected = mobilePickerMember ? draft[mobilePickerMember.id] === mentor.id : false
                return <button key={mentor.id} type="button" role="option" aria-selected={selected} onClick={() => { if (mobilePickerMember) updateAssignment(mobilePickerMember.id, mentor.id); setMobilePickerId(null) }} className={cn("flex min-h-[60px] w-full items-center gap-3 border-b border-[var(--cohort-rule-soft)] px-5 text-left", selected && "bg-[var(--cohort-orange-soft)]")}><MentorOption mentor={mentor} selected={selected} count={mentorCounts.get(mentor.id) || 0} memberLabelSingular={memberLabelSingular} memberLabelPlural={memberLabelPlural} /></button>
              })}
              {!mobilePickerMentors.length && mobilePickerQuery.trim() ? <p className="px-5 py-10 text-center text-sm text-[var(--cohort-ink-muted)]">No {mentorLabelPlural.toLowerCase()} match “{mobilePickerQuery.trim()}”.</p> : null}
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={discardOpen} onOpenChange={setDiscardOpen}>
          <SheetContent role="alertdialog" side="bottom" overlayClassName="cohort-pairings-nested-overlay" className="cohort-experience cohort-pairings-confirm-sheet !h-auto !max-h-none !gap-0 !overflow-hidden !rounded-t-[20px] !border-0 !bg-[var(--cohort-paper)] !px-5 !pb-[calc(1rem+env(safe-area-inset-bottom))] !pt-5 !text-[var(--cohort-ink)]">
            <SheetTitle className="text-[21px] font-normal tracking-[-0.015em]">Discard pairing changes?</SheetTitle>
            <SheetDescription className="mt-2 text-[15px] leading-[1.55] text-[var(--cohort-ink-muted)]">Your unsaved {mentorLabelSingular.toLowerCase()} assignments will be lost.</SheetDescription>
            {discardActions(true)}
          </SheetContent>
        </Sheet>
      </>
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) requestClose() }}>
        <DialogContent hideCloseButton closeDisabled={isSaving} className="cohort-dialog cohort-pairings-dialog !flex !h-[min(648px,calc(100dvh-4rem))] !w-[760px] !max-w-[calc(100vw-2rem)] !flex-col !gap-0 !overflow-hidden !rounded-[20px] !p-0">
          <div className="shrink-0 px-7 pt-[26px]">
            <div className="flex items-start justify-between gap-6">
              <div className="min-w-0">
                <DialogTitle className="text-[27px] font-normal leading-[1.15] tracking-[-0.02em]">Manage {mentorLabelSingular.toLowerCase()} pairings</DialogTitle>
                <DialogDescription className="mt-1.5 text-[15px] text-[var(--cohort-ink-muted)]">Assign one {mentorLabelSingular.toLowerCase()} to each eligible cohort {memberLabelSingular.toLowerCase()}.</DialogDescription>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <p aria-live="polite" className="text-right text-sm text-[var(--cohort-ink-muted)]"><span className="font-medium text-[var(--cohort-ink)]">{pairedCount} paired</span><span> · {unpairedCount} unpaired</span></p>
                <button type="button" disabled={isSaving} onClick={requestClose} className="cohort-icon-button flex h-10 w-10 items-center justify-center rounded-[11px] border border-[var(--cohort-rule)] bg-[var(--cohort-rest)]" aria-label={`Close manage ${mentorLabelSingular.toLowerCase()} pairings`}><X className="h-[17px] w-[17px]" aria-hidden="true" /></button>
              </div>
            </div>
            {members.length ? <div className="flex items-center gap-3 py-4">{renderSearch()}{renderFilters(false)}</div> : <div className="h-4" />}
          </div>
          <div className="cohort-pairings-list min-h-0 flex-1 overflow-y-auto border-y border-[var(--cohort-rule)] bg-[var(--cohort-rest)]">{listContent(false)}</div>
          <div className="shrink-0 bg-[var(--cohort-paper)] px-7 py-4">
            {phase === "error" ? <div className="mb-3"><SaveError /></div> : null}
            <div className="flex items-center justify-between gap-5">
              <p className="text-[13px] text-[var(--cohort-ink-muted)]">{isSaving ? "Saving your draft…" : hasChanges ? `${changedMemberIds.length} ${changedMemberIds.length === 1 ? "assignment" : "assignments"} changed · not saved yet` : "No changes yet"}</p>
              <div className="flex gap-2.5">
                <Button type="button" variant="outline" disabled={isSaving} onClick={requestClose} className="cohort-secondary-button min-h-12 px-5 text-base">Cancel</Button>
                <Button type="button" disabled={!hasChanges || isSaving || loading} onClick={handleSave} className="cohort-primary-button min-h-12 w-[190px] text-base">
                  {isSaving ? <Loader2 className="h-[17px] w-[17px] animate-spin" aria-hidden="true" /> : null}{isSaving ? "Saving…" : "Save pairings"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent role="alertdialog" hideCloseButton overlayClassName="cohort-pairings-confirm-overlay" className="cohort-dialog cohort-pairings-confirm !block !h-auto !w-[420px] !max-w-[calc(100vw-2rem)] !rounded-[18px] !p-6">
          <DialogTitle className="text-[22px] font-normal tracking-[-0.015em]">Discard pairing changes?</DialogTitle>
          <DialogDescription className="mt-2 text-[15px] leading-6 text-[var(--cohort-ink-muted)]">Your unsaved {mentorLabelSingular.toLowerCase()} assignments will be lost.</DialogDescription>
          {discardActions(false)}
        </DialogContent>
      </Dialog>
    </>
  )
}
